import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import TextField from "aldoo-ra/TextField"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import {
  createCheck,
  updateCheck,
  testCheckNow,
  testCheckPayload,
  captureReference,
} from "../api"
import SamplesPanel from "./SamplesPanel"

const KIND_OPTIONS = [
  { value: "ping", label: "Ping", description: "Verifies the host is reachable over the network." },
  { value: "http200", label: "HTTP 200", description: "Calls the URL and checks for the expected status code." },
  { value: "payload", label: "Payload match", description: "Calls the URL and asserts a JSON path equals a value." },
  { value: "screenshot", label: "Screenshot diff", description: "Compares a page screenshot against a saved reference." },
  { value: "tcp_tls", label: "TCP/TLS handshake", description: "Opens a TLS connection to host:port and validates the cert." },
]

const METHOD_OPTIONS = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
  { value: "HEAD", label: "HEAD" },
  { value: "OPTIONS", label: "OPTIONS" },
]

const CheckEditor = ({ componentId, check, onClose }) => {
  const isNew = !check?._id
  const [url, setUrl] = useState(check?.url || "")
  const [name, setName] = useState(check?.name || "")
  const initialKind = check?.kind || "ping"
  const [kind, setKind] = useState(KIND_OPTIONS.find((o) => o.value === initialKind) || KIND_OPTIONS[0])
  const [method, setMethod] = useState(METHOD_OPTIONS.find((o) => o.value === (check?.method || "GET")) || METHOD_OPTIONS[0])
  const [expectStatus, setExpectStatus] = useState(check?.expect?.status || 200)
  const [bodyContains, setBodyContains] = useState(check?.expect?.bodyContains || "")
  const [jsonPath, setJsonPath] = useState(check?.expect?.jsonPath || "")
  const [jsonValue, setJsonValue] = useState(
    check?.expect?.payloadMatch == null
      ? ""
      : typeof check.expect.payloadMatch === "string"
        ? check.expect.payloadMatch
        : JSON.stringify(check.expect.payloadMatch)
  )
  const [intervalSec, setIntervalSec] = useState(check?.schedule?.intervalSec || 60)
  const [timeoutMs, setTimeoutMs] = useState(check?.schedule?.timeoutMs || 10000)
  const [enabled, setEnabled] = useState(check?.schedule?.enabled ?? true)
  const [vpWidth, setVpWidth] = useState(check?.screenshot?.viewport?.width || 1280)
  const [vpHeight, setVpHeight] = useState(check?.screenshot?.viewport?.height || 800)
  const [thresholdPct, setThresholdPct] = useState(check?.screenshot?.thresholdPct ?? 2)
  const [fullPage, setFullPage] = useState(!!check?.screenshot?.fullPage)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [testStatus, setTestStatus] = useState("idle") // idle | running | ok | error
  const [savedId, setSavedId] = useState(check?._id || null)
  const [hasReference, setHasReference] = useState(!!check?.screenshot?.referenceImageId)

  const payload = () => ({
    url,
    name,
    kind: kind?.value || kind,
    method: method?.value || method,
    expect: {
      status: Number(expectStatus) || 200,
      bodyContains: bodyContains || null,
      jsonPath: jsonPath || null,
      payloadMatch: jsonValue === "" ? null : (() => {
        try { return JSON.parse(jsonValue) } catch { return jsonValue }
      })(),
    },
    screenshot: {
      viewport: { width: Number(vpWidth) || 1280, height: Number(vpHeight) || 800 },
      thresholdPct: Number(thresholdPct) || 2,
      fullPage,
    },
    schedule: {
      intervalSec: Number(intervalSec) || 60,
      timeoutMs: Number(timeoutMs) || 10000,
      enabled,
    },
  })

  const save = async () => {
    setErr(null)
    if (isNew) {
      const r = await createCheck({ componentId, ...payload() })
      if (r?.error) return setErr(r.error)
      setSavedId(r.check?._id)
      if (onClose) onClose()
    } else {
      const r = await updateCheck(check._id, payload())
      if (r?.error) return setErr(r.error)
      if (onClose) onClose()
    }
  }

  const runTest = async () => {
    setErr(null)
    setBusy(true); setTestStatus("running"); setTestResult("Running...")
    const r = savedId
      ? await testCheckNow(savedId)
      : await testCheckPayload(payload())
    setBusy(false)
    const ok = r?.sample?.ok === true
    setTestStatus(ok ? "ok" : "error")
    setTestResult(JSON.stringify(r?.sample || r, null, 2))
  }

  const onCaptureRef = async () => {
    if (!savedId) return setErr("save_first")
    setBusy(true); setTestResult("Capturing reference...")
    const r = await captureReference(savedId)
    setBusy(false)
    if (r?.error) return setErr(r.error)
    setHasReference(true)
    setTestResult(`Reference captured. id=${r.referenceId}`)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 -mt-2">
        <span className="text-sm font-medium text-text_secondary">Enabled</span>
        <ToggleSwitch isOn={enabled} onChange={setEnabled} />
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <label className="block mb-2 text-sm font-medium text-text_secondary">Check Type</label>
          <DropdownSelect
            value={kind}
            onChange={(v) => setKind(v?.[0] || v)}
            options={KIND_OPTIONS}
            renderOption={(opt) => (
              <div className="py-1">
                <div className="text-sm font-medium text-text">{opt.label}</div>
                {opt.description && (
                  <div className="text-xs text-text_secondary mt-0.5">{opt.description}</div>
                )}
              </div>
            )}
          />
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <TextField
              label="URL"
              value={url}
              onChange={(v) => { setUrl(v); setTestStatus("idle") }}
              showProgress={testStatus === "running"}
              showCompleted={testStatus === "ok"}
              showError={testStatus === "error"}
            />
          </div>
          {kind?.value !== "screenshot" && (
            <Button variant="secondary" onClick={runTest} disabled={busy || !url}>Test Now</Button>
          )}
        </div>
        <TextField label="Label (optional)" value={name} onChange={setName} collapsible />

        {(kind?.value === "http200" || kind?.value === "payload") && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-2 text-sm font-medium text-text_secondary">Method</label>
              <DropdownSelect value={method} onChange={(v) => setMethod(v?.[0] || v)} options={METHOD_OPTIONS} />
            </div>
            <TextField label="Expect status" type="number" value={expectStatus} onChange={setExpectStatus} />
          </div>
        )}

        {kind?.value === "payload" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="JSON path" value={jsonPath} onChange={setJsonPath} placeholderText="data.status" />
              <TextField label="Expected value" value={jsonValue} onChange={setJsonValue} placeholderText="ok" />
            </div>
            <TextField label="Body contains (optional)" value={bodyContains} onChange={setBodyContains} collapsible />
          </>
        )}

        {kind?.value === "screenshot" && (
          <>
            <TextField label="Viewport width" type="number" value={vpWidth} onChange={setVpWidth} />
            <TextField label="Viewport height" type="number" value={vpHeight} onChange={setVpHeight} />
            <TextField label="Diff threshold %" type="number" value={thresholdPct} onChange={setThresholdPct} />
            <Checkbox label="Full page" isOn={fullPage} onChange={setFullPage} />
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <TextField label="Interval (sec)" type="number" value={intervalSec} onChange={setIntervalSec} />
          <TextField label="Timeout (ms)" type="number" value={timeoutMs} onChange={setTimeoutMs} />
        </div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <div className="flex gap-2 justify-center flex-wrap">
          <Button onClick={save} disabled={busy} className="px-10 py-3 text-base">{isNew ? "Create" : "Save"}</Button>
          {kind?.value === "screenshot" && (
            <Button variant="secondary" onClick={onCaptureRef} disabled={busy || !savedId} className="px-10 py-3 text-base">
              {hasReference ? "Replace reference" : "Capture reference"}
            </Button>
          )}
        </div>
        {err && <div className="text-error text-sm">Error: {err}</div>}
      </div>

      {testResult && (
        <pre className="p-3 rounded bg-canvas border border-bordered text-xs overflow-auto max-h-64 whitespace-pre-wrap">{testResult}</pre>
      )}

      {savedId && kind?.value === "screenshot" && (
        <div className="border-t border-bordered pt-4 mt-4">
          <SamplesPanel checkId={savedId} />
        </div>
      )}
    </div>
  )
}

export default CheckEditor
